import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import LiveDataStrip from "@/components/LiveDataStrip";
import PublicNav from "@/components/landing/PublicNav";
import PublicFooter from "@/components/landing/PublicFooter";
import HeroBackdrop from "@/components/landing/HeroBackdrop";
import LiveCampaignCard from "@/components/landing/LiveCampaignCard";
import ContactSection from "@/components/landing/ContactSection";
import DamaniHoldingsSection from "@/components/landing/DamaniHoldingsSection";
import FamilyOfCompaniesSection from "@/components/landing/FamilyOfCompaniesSection";
import OperatorsSection from "@/components/landing/OperatorsSection";
import PlacementProductsSection from "@/components/landing/PlacementProductsSection";
import { trackPageview, trackShareClick } from "@/lib/analytics";
import { FORTUNE_PRICE, FULL_MONTHLY, TRIAL_DAYS, TRIAL_SHORT, statesLabel, usd } from "@/lib/pricing";
import { PhoneCall, ArrowRight, Database, PhoneOutgoing, CalendarCheck } from "lucide-react";

const compact = (n) => (typeof n !== "number" ? null : n >= 1000 ? `${Math.floor(n / 1000)}K` : `${n}`);

const STEPS = [
  [Database, "Pick your state and venues", "Every eligible c-store, bar, truck stop, laundromat, restaurant or vape shop in your state — with the owner's verified phone and email."],
  [PhoneOutgoing, "Approve, then Lucky calls and we email", "Choose 10, 20 or 50 owners. Preview exactly who gets contacted, approve the batch, and Lucky (our AI caller) dials while the email goes out. Nothing is sent without your approval."],
  [CalendarCheck, "Take the callbacks, sign the placement", "Interested owners land on your Callbacks list with a transcript and summary. You call back, visit, and place the machine."],
];

// Homepage — operator-first: who it's for, what they get, the price and the trial above the fold; the two live campaigns as proof;
// how it works; live database counters; contact. Referral chain (?ref=) and the /fleet variant are preserved.
export default function OperatorLanding({ fleetMode = false } = {}) {
  const [rep, setRep] = useState({});
  const [stats, setStats] = useState(() => globalThis.__AO_STATS__ || null);
  const [home, setHome] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    trackPageview(fleetMode ? "/fleet" : "/");
    api.get("/sales-rep").then((r) => setRep(r.data || {})).catch(() => {});
    api.get("/public/data-stats").then((r) => setStats(r.data || null)).catch(() => {});
    api.get("/public/homepage").then((r) => setHome(r.data || null)).catch(() => {});
  }, [fleetMode]);

  const phone = rep.phone || "+1-325-221-4234";
  const email = rep.email || "sales@arcadeops.net";
  const name = rep.name || "Aziz Damani";
  const ref = rep.referral_code || "AZIZ-2026";
  const inquiryRef = searchParams.get("ref") || ref;
  const pills = [
    [compact(stats?.total) ? `${compact(stats.total)}+` : null, "venues in the database"], [compact(stats?.gold), "owner emails verified"],
    [compact(stats?.phones), "direct phone lines"], [statesLabel(stats?.states), "states covered"],
  ].filter(([v]) => v);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicNav />

      {fleetMode && (
        <div className="bg-emerald-500/10 border-b border-emerald-500/40" data-testid="fleet-banner">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-emerald-400">Fuel Jobbers · Fleets · Multi-Store Operators</div>
              <div className="mt-1 font-heading font-bold text-lg sm:text-xl text-white">Free placement + <span className="text-emerald-400">revenue share</span> across your entire network.</div>
              <div className="mt-1 text-xs sm:text-sm text-white/75">Zero cap-ex. One master agreement covers every c-store. We install, service, and pay you monthly.</div>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <a href={`tel:${phone}`} data-testid="fleet-banner-call" className="inline-flex items-center gap-2 bg-emerald-500 text-black hover:bg-white transition-colors font-heading font-black px-4 py-2.5 uppercase tracking-widest text-xs"><PhoneCall className="w-3.5 h-3.5" /> Call us</a>
            </div>
          </div>
        </div>
      )}

      <section id="hero" data-testid="landing-hero-container" className="relative overflow-hidden">
        <HeroBackdrop />
        <div className="relative max-w-7xl mx-auto px-4 pt-12 sm:pt-16 lg:pt-20 pb-12 sm:pb-16 grid lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          <div className="lg:col-span-6 lg:pt-4">
            <div className="rise font-mono text-[10px] uppercase tracking-[0.28em] text-neon-cyan" data-testid="hero-kicker">
              {fleetMode ? "Arcade Ops · Fleet & fuel-jobber partnership" : "Arcade Ops · For amusement & skill-game operators"}
            </div>
            <h1 data-testid="hero-headline" className="rise mt-4 font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight" style={{ animationDelay: "80ms" }}>
              {fleetMode
                ? <><span className="text-emerald-400">$0 upfront.</span> One deal, every store.</>
                : <>The placement engine for <span className="text-neon-yellow">amusement &amp; skill-game operators.</span></>}
            </h1>
            <p data-testid="hero-subtitle" className="rise mt-5 text-base sm:text-lg text-white/70 leading-relaxed max-w-xl" style={{ animationDelay: "160ms" }}>
              {fleetMode
                ? "Fortune Games machines placed free across your fleet. We install, service, insure. You keep a monthly revenue split — paid on the 1st of every month."
                : "Verified owner contacts for every eligible venue in your state, plus approve-before-send AI calls and email that book the owner conversations. Place Fortune skill game cabinets, AOG Sweeps game kiosks and ATM kiosks in c-stores, gas stations and bars from one screen."}
            </p>

            {!fleetMode && (
              <div className="rise mt-6 border border-neon-yellow/50 bg-black/40 px-4 py-3 inline-flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="hero-price" style={{ animationDelay: "200ms" }}>
                <span className="font-heading font-black text-2xl sm:text-3xl text-neon-yellow tabular-nums">{usd(FULL_MONTHLY)}<span className="text-sm text-white/70 font-mono font-normal">/mo per state</span></span>
                <span className="font-mono text-[11px] uppercase tracking-widest text-white/75" data-testid="hero-trial-policy">{TRIAL_SHORT}</span>
              </div>
            )}

            <div className="rise mt-6 flex flex-wrap items-center gap-3" style={{ animationDelay: "280ms" }}>
              {fleetMode ? (
                <a href="#inquire" data-testid="hero-contact-cta" className="inline-flex items-center gap-2 h-12 px-6 bg-neon-yellow text-black hover:bg-white transition-colors font-heading font-bold text-sm uppercase tracking-wide">Contact us <ArrowRight className="w-4 h-4" /></a>
              ) : (
                <>
                  <Link to="/login?mode=register&plan=trial" data-testid="hero-trial-cta" onClick={() => trackShareClick("hero-trial", { source: "operator_landing" })} className="inline-flex items-center gap-2 h-12 px-6 bg-neon-yellow text-black hover:bg-white transition-colors font-heading font-bold text-sm uppercase tracking-wide">
                    Start {TRIAL_DAYS}-day trial <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/pricing" data-testid="hero-pricing-cta" className="inline-flex items-center gap-2 h-12 px-5 border border-border hover:border-neon-yellow text-white/85 hover:text-neon-yellow transition-colors font-mono text-xs uppercase tracking-widest">See pricing</Link>
                </>
              )}
              <a href={`tel:${phone}`} data-testid="hero-call-link" className="inline-flex items-center gap-2 h-12 px-4 border border-border hover:border-neon-yellow text-white/85 hover:text-neon-yellow transition-colors font-mono text-xs uppercase tracking-widest">
                <PhoneCall className="w-4 h-4" /> Call · {phone}
              </a>
            </div>

            {!fleetMode && pills.length > 0 && (
              <ul className="rise mt-7 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-2xl" data-testid="hero-pills" style={{ animationDelay: "340ms" }}>
                {pills.map(([v, l]) => (
                  <li key={l} className="border border-border/70 bg-black/30 px-3 py-2">
                    <div className="font-heading font-black text-xl tabular-nums text-white">{v}</div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/50">{l}</div>
                  </li>
                ))}
              </ul>
            )}
            {!fleetMode && (
              <p className="rise mt-6 text-sm text-white/55" data-testid="hero-secondary-line" style={{ animationDelay: "400ms" }}>
                Not placing machines? The same database and outreach run campaigns for whatever you sell to independent retail — <a href="#inquire" data-testid="hero-secondary-link" className="text-neon-cyan hover:text-white underline-offset-4 hover:underline">tell us what you sell</a>.
              </p>
            )}
          </div>

          <div id="campaigns" className="lg:col-span-6 scroll-mt-20" data-testid="hero-live-campaigns">
            <div className="rise flex items-center justify-between mb-3" style={{ animationDelay: "200ms" }}>
              <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-neon-yellow">
                <span className="inline-block w-1.5 h-1.5 bg-neon-yellow rounded-full animate-pulse" /> Live campaigns · built and running by Arcade Ops
              </div>
              <span className="hidden sm:block font-mono text-[10px] uppercase tracking-widest text-white/40">02 running</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <LiveCampaignCard
                id="campaign-card-fortune-games" index={1} accent="yellow" delay={260}
                title="Fortune Games" tag={`Skill-based cabinets · ${FORTUNE_PRICE}`}
                pitch="A 43-inch Fortune Games skill-based cabinet with a 6-game pack (6–7 packs, new pack every 3 months), sold outright to venue owners. ATM Kiosks $6,000 each. Flyer, landing page and outreach all built by Arcade Ops."
                highlights={[`${FORTUNE_PRICE} · 1-year parts & labor`, "6-game pack included · boards $2,000, no volume pricing", "Scan the QR to request a quote — no sign-in"]}
                image={home?.fortune_card_image_url || "/campaigns/fortune-card-product.jpg"} imageAlt="Fortune Games 43-inch cabinet" imagePosition="center"
                qr={`${API}/public/fortune/qr.png`} href="/fortune" viewTestId="campaign-fortune-view-btn"
              />
              <LiveCampaignCard
                id="campaign-card-aog-kiosk" index={2} accent="cyan" delay={360}
                title="AOG Coin kiosk" tag="Free counter placement"
                pitch="A free AOG sign-up kiosk for the counter — nothing for the venue to buy. Marketed and placed by Arcade Ops in eligible states."
                highlights={["Nothing to buy, nothing to install yourself", "Vape shops, gas stations, c-stores, grocers, bars", "Scan the QR to open the kiosk sign-up page"]}
                image={`${API}/campaigns/public/aog-coin/asset/kiosk_photo`} imageFallback="/campaigns/aog-kiosk-card.jpg" imageAlt="AOG Coin sign-up kiosk on a store counter"
                qr={`${API}/campaigns/public/aog-coin/qr.png`} href="/aog-coin" viewTestId="campaign-aog-view-btn"
              />
            </div>
          </div>
        </div>
      </section>

      <DamaniHoldingsSection />

      {!fleetMode && <PlacementProductsSection />}

      {!fleetMode && (
        <section id="how-it-works" className="border-t border-border/60 bg-card/30 scroll-mt-20" data-testid="how-it-works">
          <div className="max-w-7xl mx-auto px-4 py-14 sm:py-16">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-neon-cyan">How it works</div>
            <h2 className="mt-2 font-heading font-black text-base md:text-lg text-white max-w-2xl">Three steps from a state to a signed placement — and nothing is sent without your approval.</h2>
            <ol className="mt-8 grid md:grid-cols-3 gap-4">
              {STEPS.map(([Icon, title, body], i) => (
                <li key={title} className="border border-border/70 bg-black/30 p-5" data-testid={`how-step-${i + 1}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 border border-neon-cyan/60 text-neon-cyan flex items-center justify-center font-heading font-black">{i + 1}</span>
                    <Icon className="w-4 h-4 text-neon-cyan" />
                  </div>
                  <div className="mt-3 font-heading font-bold text-foreground">{title}</div>
                  <p className="mt-1.5 text-sm text-white/65 leading-relaxed">{body}</p>
                </li>
              ))}
            </ol>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/login?mode=register&plan=trial" data-testid="how-trial-cta" className="inline-flex items-center gap-2 h-11 px-5 bg-neon-yellow text-black hover:bg-white transition-colors font-heading font-bold text-sm uppercase tracking-wide">Start {TRIAL_DAYS}-day trial <ArrowRight className="w-4 h-4" /></Link>
              <span className="font-mono text-[11px] text-white/55" data-testid="how-trial-policy">{TRIAL_SHORT}</span>
            </div>
          </div>
        </section>
      )}

      <div data-testid="live-data-strip"><LiveDataStrip /></div>

      <OperatorsSection />

      <ContactSection name={name} phone={phone} email={email} inquiryRef={inquiryRef} />

      <FamilyOfCompaniesSection />

      <PublicFooter />
    </div>
  );
}
